import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { HubConnection } from '@microsoft/signalr'
import { acquire, release } from './connection'
import { liveKeys } from './queries'
import type { LiveSession, Ping } from './types'

/** How long a ping marker stays on the map before it fades. Long enough to look at, short enough to mean "now". */
const PING_MS = 2000

/**
 * Watches one session over the hub.
 *
 * **The hub is a cache writer, not a state store.** Every `SessionState` push lands in the same
 * React Query entry `GET /live-sessions/{id}` populates, so the screen renders from one source
 * whichever arrived last — and a hub that never connects degrades to a page that works and does
 * not update itself.
 *
 * A reconnect **re-reads** rather than trusting what it was holding: the client may have missed
 * any number of pushes while it was away, and the server's state is the only authority. That is
 * the reverse of an optimistic merge, and it is correct because there is exactly one writer.
 */
export function useLiveSessionStream(
  sessionId: string,
  enabled: boolean,
): { ended: boolean; ping: Ping | null } {
  const queryClient = useQueryClient()
  const [ended, setEnded] = useState(false)
  const [ping, setPing] = useState<Ping | null>(null)

  // The ping's own timer, cleared on unmount so a marker cannot outlive the screen.
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!enabled || sessionId === '') return

    let cancelled = false
    let connection: HubConnection | null = null

    setEnded(false)

    const onState = (session: LiveSession) => {
      if (cancelled) return
      queryClient.setQueryData<LiveSession>(liveKeys.session(session.sessionId), session)
    }

    const onEnded = (payload: { sessionId: string }) => {
      if (cancelled || payload.sessionId !== sessionId) return

      setEnded(true)
      void queryClient.invalidateQueries({ queryKey: liveKeys.session(sessionId) })
      void queryClient.invalidateQueries({ queryKey: liveKeys.list() })
    }

    const onPinged = (payload: Ping) => {
      if (cancelled) return

      setPing(payload)
      if (pingTimer.current !== null) clearTimeout(pingTimer.current)
      pingTimer.current = setTimeout(() => setPing(null), PING_MS)
    }

    /**
     * Joins the session's group and takes whatever state comes back.
     *
     * Also the reconnect path: group membership does not survive a reconnect, and whatever
     * changed while the socket was down is not going to be pushed again.
     */
    const join = (active: HubConnection) => {
      void active
        .invoke<LiveSession | null>('JoinSession', sessionId)
        .then((session) => {
          if (!cancelled && session !== null) onState(session)
        })
        .catch(() => {
          // A failed join costs live updates, not the screen — the REST read already filled the
          // cache. Invalidating asks for the current truth rather than leaving a stale map up.
          if (!cancelled) {
            void queryClient.invalidateQueries({ queryKey: liveKeys.session(sessionId) })
          }
        })
    }

    void (async () => {
      const active = await acquire()

      if (cancelled) {
        release()
        return
      }

      connection = active

      active.on('SessionState', onState)
      active.on('SessionEnded', onEnded)
      active.on('Pinged', onPinged)
      active.onreconnected(() => join(active))

      join(active)
    })()

    return () => {
      cancelled = true

      if (pingTimer.current !== null) clearTimeout(pingTimer.current)

      if (connection === null) {
        // The acquire above never resolved, so its `release` runs in that branch instead.
        return
      }

      connection.off('SessionState', onState)
      connection.off('SessionEnded', onEnded)
      connection.off('Pinged', onPinged)

      void connection.invoke('LeaveSession', sessionId).catch(() => undefined)
      release()
    }
  }, [sessionId, enabled, queryClient])

  return { ended, ping }
}

/** Sends the Game Master's ping. A no-op when the hub is not up — it is a nicety, not a write. */
export async function sendPing(sessionId: string, x: number, y: number): Promise<void> {
  const { current } = await import('./connection')
  const connection = current()

  if (connection === null) return

  try {
    await connection.invoke('Ping', sessionId, x, y)
  } catch {
    // Nothing is stored by a ping, so a failed one has no consequence worth reporting.
  }
}
